import type { AiExecutionPlan, AiExecutionPlanStep, AiOperationalPlan } from '../../../types/aiOperational'

export interface AiExecutionTheaterCue {
  id: string
  title: string
  description: string
  authorityLabel: string
  status: AiExecutionPlanStep['status']
  requiresUserAction: boolean
}

export interface AiExecutionTheaterOutline {
  title: string
  summary: string
  riskLabel: string
  graphStateLabel?: string
  autoStartLabel: string
  checkpointLabel: string
  checkpointSummaries: string[]
  cues: AiExecutionTheaterCue[]
}

const authorityLabel = (authority: AiExecutionPlanStep['authority']) => {
  switch (authority) {
    case 'CLIENT_LOCAL':
      return 'Local guidance'
    case 'USER_CONFIRMATION':
      return 'User confirmation'
    case 'AI_PROPOSAL_REVIEW':
      return 'Proposal review'
    case 'TRUSTED_BACKEND':
      return 'Trusted backend'
    case 'DELTA_PROCESSOR':
      return 'DeltaProcessor'
    case 'AUDIT_LEDGER':
      return 'Audit ledger'
    default:
      return authority
  }
}

const checkpointLabel = (executionPlan: AiExecutionPlan) => {
  if (!executionPlan.checkpoints.length) return 'No checkpoints required for local guidance.'
  return executionPlan.checkpoints
    .slice(0, 3)
    .map((checkpoint) => checkpoint.title)
    .join(' • ')
}

export const buildExecutionTheaterOutline = (plan: AiOperationalPlan): AiExecutionTheaterOutline | null => {
  const executionPlan = plan.envelope.executionPlan
  if (!executionPlan) return null
  const graph = plan.envelope.executionGraph

  return {
    title: executionPlan.title,
    summary: executionPlan.summary,
    riskLabel: `${executionPlan.risk.toLowerCase()} risk`,
    ...(graph ? { graphStateLabel: graph.state.toLowerCase().replace(/_/g, ' ') } : {}),
    autoStartLabel: executionPlan.canAutoStart ? 'Can start as local guidance' : 'Requires review or confirmation',
    checkpointLabel: checkpointLabel(executionPlan),
    checkpointSummaries: (graph?.checkpoints ?? executionPlan.checkpoints).slice(0, 4).map((checkpoint) =>
      `${checkpoint.title}: ${(checkpoint.state ?? 'PENDING').toLowerCase()}`),
    cues: executionPlan.steps.slice(0, 6).map((step) => ({
      id: step.id,
      title: step.title,
      description: step.description,
      authorityLabel: authorityLabel(step.authority),
      status: step.status,
      requiresUserAction: step.requiresUserAction,
    })),
  }
}
